import { api } from './config.js'

// Longer than config.js's 60s default on purpose: this is the one request shape that legitimately
// runs past it. Arithmetic: src/services/README.md. What a client timeout does and does not settle
// on the backend side: src/pages/README.md.
const ARV_SEARCH_TIMEOUT_MS = 80000

export const reilmAPI = {
    createUser : async function (body) {
        const response = await api.request({
            url: '/users',
            method: "POST",
            data: body
        })

        return response;
    },

    loginUser : async function (body) {
        const response = await api.request({
            url: '/auth/login',
            method: "POST",
            data: body
        })

        return response;
    },

    getCurrentUser : async function () {
        const response = await api.request({
            url: '/auth/me',
            method: "GET",
        })

        return response;
    },

    logoutUser : async function () {
        const response = await api.request({
            url: '/auth/logout',
            method: "POST",
        })

        return response;
    },

    refreshToken : async function () {
        const response = await api.request({
            url: '/auth/refresh',
            method: "POST",
        })

        return response;
    },

    createSubscription : async function (body) {
        const response = await api.request({
            url: '/stripe/create-subscription',
            method: "POST",
            data: body
        })

        return response;
    },

    getSubscriptionStatus : async function () {
        const response = await api.request({
            url: '/stripe/subscription-status',
            method: "GET",
        })

        return response;
    },

    forgotPassword : async function (body) {
        const response = await api.request({
            url: '/auth/forgot-password',
            method: "POST",
            data: body
        })

        return response;
    },

    resetPassword : async function (body) {
        const response = await api.request({
            url: '/auth/reset-password',
            method: "POST",
            data: body
        })

        return response;
    },

    getUser : async function (body) {
        const response = await api.request({
            url: '/get-user',
            method: "POST",
            data: body
        })

        return response;
    },

    savePropertyDetails : async function (body) {
        const response = await api.request({
            url: '/properties',
            method: "POST",
            data: body
        })

        return response;
    },

    updatedPropertiesDetails : async function (id, body) {
        const response = await api.request({
            url: `/properties/${id}`,
            method: "PATCH",
            data: body
        })

        return response;
    },

    archivePropertiesBatch : async function (body) {
        const response = await api.request({
            url: '/properties/bulk/archive',
            method: "PATCH",
            data: body
        })

        return response;
    },

    deleteProperty : async function (id) {
        const response = await api.request({
            url: `/properties/${id}`,
            method: "DELETE",
        })

        return response;
    },

    deletePropertiesBatch : async function (body) {
        const response = await api.request({
            url: '/properties/bulk',
            method: "DELETE",
            data: body
        })

        return response;
    },

    getTags: async function () {
        const response = await api.request({
            url: '/tags',
            method: "GET",
        })
        return response;
    },

    createTag: async function (body) {
        const response = await api.request({
            url: '/tags',
            method: "POST",
            data: body,
        })
        return response;
    },

    updateTag: async function (id, body) {
        const response = await api.request({
            url: `/tags/${id}`,
            method: "PATCH",
            data: body,
        })
        return response;
    },

    deleteTag: async function (id) {
        const response = await api.request({
            url: `/tags/${id}`,
            method: "DELETE",
        })
        return response;
    },

    getProperties: async function () {
        const response = await api.request({
            url: '/properties',
            method: "GET",
        })
        return response;
    },

    getProperty: async function (id) {
        const response = await api.request({
            url: `/properties/${id}`,
            method: "GET",
        })
        return response;
    },

    // encodeURIComponent on compId, not on propertyId: a propertyId is a Mongo ObjectId, but a comp
    // id is the RentCast listing key, which IS an address — `1954-Glenmar-Dr,-Decatur,-GA-30032`.
    // Commas survive a path segment unescaped, so these two calls worked; a unit number (`#`) or a
    // street written with a slash would not, and it would arrive as a different route entirely.
    toggleArvComp: async function (propertyId, compId, archived) {
        const response = await api.request({
            url: `/properties/${propertyId}/arv/comps/${encodeURIComponent(compId)}`,
            method: 'PATCH',
            data: { archived },
        })
        return response
    },

    enrichArvComp: async function (propertyId, compId) {
        const response = await api.request({
            url: `/properties/${propertyId}/arv/comps/${encodeURIComponent(compId)}/enrich`,
            method: 'PATCH',
        })
        return response
    },

    searchArvComps: async function (id, criteria) {
        const response = await api.request({
            url: `/properties/${id}/arv/search`,
            method: 'POST',
            data: criteria,
            timeout: ARV_SEARCH_TIMEOUT_MS,
        })
        return response
    },

    // Recomputes score/adjustments for every saved comp against the four criteria fields, no
    // provider calls -- distinct from searchArvComps, which fetches a fresh set. Shares its timeout
    // override as the second half of the same round trip in Arv.jsx, not its own measurement.
    recomputeArvCriteria: async function (id, criteria) {
        const response = await api.request({
            url: `/properties/${id}/arv/criteria`,
            method: 'PATCH',
            data: criteria,
            timeout: ARV_SEARCH_TIMEOUT_MS,
        })
        return response
    },

    searchAddress: async function (body) {
        const response = await api.request({
            url: '/realtor/autocomplete',
            method: 'GET',
            params: { input: body },
        })
        return response;
    },

    realtorPropertyDetail: async function (body) {
        const response = await api.request({
            url: '/realtor/detail',
            method: 'GET',
            params: { propertyId: body },
        })
        return response;
    },

    // Mounted at /api/flip-templates, not nested under /api/users (reilm-be/modules/users/README.md,
    // "why flip-templates has its own route prefix" -- keeps templates sessions out of the auth
    // rate limiter bucket). Confirmed against reilm-be/modules/users/{controller,flipTemplateFields}.js.
    getFlipTemplates: async function () {
        const response = await api.request({
            url: '/flip-templates',
            method: 'GET',
        })
        return response;
    },

    createFlipTemplate: async function (body) {
        const response = await api.request({
            url: '/flip-templates',
            method: 'POST',
            data: body,
        })
        return response;
    },

    updateFlipTemplate: async function (id, body) {
        const response = await api.request({
            url: `/flip-templates/${id}`,
            method: 'PATCH',
            data: body,
        })
        return response;
    },

    deleteFlipTemplate: async function (id) {
        const response = await api.request({
            url: `/flip-templates/${id}`,
            method: 'DELETE',
        })
        return response;
    },

    // Copies the stored values of one saved scenario into an existing template; the server reads the
    // scenario itself (D21), the client sends only which one. 409 FLIP_TEMPLATE_TYPE_MISMATCH when
    // the scenario's type differs from the template's.
    saveFlipTemplateValues: async function (templateId, { propertyId, scenarioId }) {
        const response = await api.request({
            url: `/flip-templates/${templateId}/values`,
            method: 'PUT',
            data: { propertyId, scenarioId },
        })
        return response;
    },

    // One step at a time, server-authoritative -- not a whole-array write. The server computes the
    // swap itself and returns the full resulting list; a boundary move (already first/last) is a
    // 200 no-op, not an error (reilm-be/modules/users/README.md, "Reorder is a swap, computed
    // server-side").
    reorderFlipTemplate: async function (id, direction) {
        const response = await api.request({
            url: `/flip-templates/${id}/reorder`,
            method: 'PATCH',
            data: { direction },
        })
        return response;
    },

    // Property.flipAnalysis scenario writes. Create takes template ids only: the server copies each
    // template's name, type and values (scenarios-cash-spec.28.09.md §7.1), in request order.
    createFlipScenarios: async function (id, templateIds) {
        const response = await api.request({
            url: `/properties/${id}/flip/scenarios`,
            method: 'POST',
            data: { templateIds },
        })
        return response;
    },

    // `keepalive` is for the autosave flush on tab close / hide: the XHR adapter's request dies with
    // the page, the fetch adapter's keepalive one does not. Still this instance, so the Bearer header
    // and the interceptors apply as to every other call.
    updateFlipScenario: async function (id, scenarioId, fields, { keepalive = false, signal } = {}) {
        const response = await api.request({
            url: `/properties/${id}/flip/scenarios/${scenarioId}`,
            method: 'PATCH',
            data: fields,
            signal,
            ...(keepalive ? { adapter: 'fetch', fetchOptions: { keepalive: true } } : {}),
        })
        return response;
    },

    deleteFlipScenario: async function (id, scenarioId) {
        const response = await api.request({
            url: `/properties/${id}/flip/scenarios/${scenarioId}`,
            method: 'DELETE',
        })
        return response;
    },

    duplicateFlipScenario: async function (id, scenarioId) {
        const response = await api.request({
            url: `/properties/${id}/flip/scenarios/${scenarioId}/duplicate`,
            method: 'POST',
        })
        return response;
    },

    // Read-only, stateless: body is one scenario (features/flip/computePayload.js builds it), response
    // `{ data: { computed } }`. `signal` lets useFlipCompute abort a superseded request.
    computeFlip: async function (id, scenario, { signal } = {}) {
        const response = await api.request({
            url: `/properties/${id}/flip/compute`,
            method: 'POST',
            data: scenario,
            signal,
        })
        return response;
    },

}